import { MORE_IDS, destination, type DestinationId } from "@/lib/destinations";

import { formatBadgeCount } from "./badgeCount";

/** What each row says it holds — enough to choose without opening it. */
const DETAIL: Record<string, string> = {
  search: "辞書 · 部品",
  setup: "デッキ · タグ · ジョブ",
};

interface MoreSheetProps {
  active: DestinationId;
  onNavigate: (id: DestinationId) => void;
  onClose: () => void;
  /** Jobs whose last run failed. Drawn on 設定's row, and on その他 in the bar. */
  failedJobs?: number;
}

/**
 * その他 — what the phone tab bar's fifth item opens (日本-497).
 *
 * Six destinations do not fit across 390px, so the two `MORE_IDS` names live here. A sheet holding
 * Stats, Setup and Grammar existed until 8 Aug 2026 and was deleted when Mining's removal left five;
 * this is not that sheet coming back unchanged.
 *
 * **What that deletion was really about was `more` being a tab that was not a place** — it could not
 * be serialized, `destination()` threw on it, and `lastRealTab` existed to remember where the URL
 * pointed while it was open. None of that is here: this is an **overlay** (`OverlayId: "more"`, see
 * `navHistory.ts`), so `NavState.tab` never stops naming a real destination, Back closes the sheet
 * rather than leaving the surface, and a reload lands on the surface with no sheet at all.
 *
 * **No leading glyph.** The old sheet drew one (`計 Stats`, `設 Setup`), which worked while the
 * labels were English; in Japanese 検 検索 is a stutter. The rail and the tab bar already settle
 * this — *the label is the icon* — so the word carries itself.
 */
export function MoreSheet({ active, onNavigate, onClose, failedJobs }: MoreSheetProps) {
  return (
    <div
      data-check="more.sheet"
      role="dialog"
      aria-modal="true"
      aria-label="その他"
      // **`bottom-full`, so the sheet sits ABOVE the tab bar rather than over it.** The containing
      // block is the footer wrapper, which holds the bar — at `bottom-0` the sheet covered it,
      // and その他 is a toggle whose whole affordance is being pressable again to close.
      className="absolute inset-x-0 bottom-full z-[var(--z-modal)] flex flex-col"
    >
      {/* The scrim stops at the sheet: the tab bar below stays live, because その他 is a toggle and
          you must be able to press it again to close. */}
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        data-check="more.scrim"
        className="absolute inset-x-0 bottom-full h-screen bg-[color:var(--sheet-scrim)]"
      />
      <div className="relative flex flex-col gap-[10px] rounded-t-[var(--r-3)] border-t border-line bg-sunken px-[14px] pb-[18px] pt-[10px] box-border shadow-[var(--sh-sheet)]">
        {/* The same grip the bottom sheet uses everywhere else, so it reads as the same object. */}
        <div className="flex justify-center pb-[6px] pt-1">
          <span aria-hidden className="h-[3px] w-[34px] rounded-full bg-line-strong" />
        </div>

        {MORE_IDS.map((id) => {
          const d = destination(id);
          const count = id === "setup" ? failedJobs : undefined;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onNavigate(id)}
              data-check={`more.${id}`}
              aria-current={active === id ? "page" : undefined}
              aria-label={d.label}
              className={[
                "flex min-h-[54px] items-center gap-3 rounded-[var(--r-3)] border bg-panel p-[14px] text-left box-border",
                active === id ? "border-accent-line" : "border-line-strong",
              ].join(" ")}
            >
              <span className="flex-1 font-jp text-[16px]/[1] font-semibold tracking-[.03em] text-ink">
                {d.word}
              </span>
              <span className="font-jp text-meta text-ink-4">{DETAIL[id]}</span>
              {count !== undefined && count > 0 && (
                <span
                  data-check={`more.badge.${id}`}
                  title={count.toLocaleString()}
                  className="min-w-[24px] rounded-[var(--r-2)] bg-accent-nobori px-1 text-center font-mono-jp text-[12px]/[16px] font-semibold text-white"
                >
                  {formatBadgeCount(count)}
                </span>
              )}
              <span aria-hidden className="text-meta text-ink-4">
                ›
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
